/**
 * Handles smooth scrolling to sections when clicking navbar links with hash URLs
 */
class ScrollToSection {
  constructor() {
    this.headerHeight = 0;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Calculate header height
    this.updateHeaderHeight();
    
    // Listen for header height changes (e.g., sticky header)
    this.observeHeaderHeight();
    
    // Handle clicks on menu links
    this.handleMenuLinks();
    
    // Handle hash links on page load
    this.handleInitialHash();
  }

  /**
   * Update the header height for scroll offset calculation
   */
  updateHeaderHeight() {
    const header = document.querySelector('header, [id="header-group"]');
    if (header) {
      this.headerHeight = header.offsetHeight || 0;
    }
  }

  /**
   * Observe header height changes (for sticky headers)
   */
  observeHeaderHeight() {
    const header = document.querySelector('header, [id="header-group"]');
    if (!header) return;

    const resizeObserver = new ResizeObserver(() => {
      this.updateHeaderHeight();
    });

    resizeObserver.observe(header);
  }

  /**
   * Close the mobile menu drawer
   */
  closeMobileMenu() {
    // Check if we're on mobile
    if (window.innerWidth >= 750) return;
    
    // Find the header-drawer custom element
    const headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer && headerDrawer.isOpen) {
      headerDrawer.close();
    }
  }

  /**
   * Handle clicks on menu links that contain hash fragments
   */
  handleMenuLinks() {
    // Get all menu links
    const menuLinks = document.querySelectorAll(
      'header-menu a[href^="#"], [header-menu] a[href^="#"], .menu-list__link[href^="#"], .menu-list__item[href^="#"]'
    );

    menuLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        // Only handle hash links that aren't empty
        if (href && href.startsWith('#') && href.length > 1) {
          e.preventDefault();
          const targetId = href.slice(1);
          this.scrollToSection(targetId);
        }
      });
    });

    // Also handle navigation bar links (mobile)
    const navBarLinks = document.querySelectorAll(
      '.menu-list--mobile a[href^="#"]'
    );

    navBarLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        if (href && href.startsWith('#') && href.length > 1) {
          e.preventDefault();
          const targetId = href.slice(1);
          this.scrollToSection(targetId);
          this.closeMobileMenu();
        }
      });
    });

    // Handle all menu drawer links on mobile - close drawer when any link is clicked
    const menuDrawerLinks = document.querySelectorAll(
      '.menu-drawer a[href], .menu-drawer__menu-item a[href]'
    );

    menuDrawerLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        // Handle hash links that scroll to sections
        if (href && href.startsWith('#') && href.length > 1) {
          e.preventDefault();
          const targetId = href.slice(1);
          this.scrollToSection(targetId);
        }
        
        // Close mobile menu after a short delay to allow scroll to start
        // This ensures the menu closes even if the link triggers scrolling
        setTimeout(() => {
          this.closeMobileMenu();
        }, 100);
      });
    });
  }

  /**
   * Scroll to a specific section by ID
   * @param {string} sectionId - The ID of the section to scroll to
   */
  scrollToSection(sectionId) {
    const targetElement = document.getElementById(sectionId);
    
    if (!targetElement) {
      console.warn(`Section with ID "${sectionId}" not found`);
      return;
    }

    // Calculate the scroll position with header offset
    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - this.headerHeight;

    // Smooth scroll to the section
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Update URL hash without triggering scroll
    if (history.pushState) {
      history.pushState(null, null, `#${sectionId}`);
    } else {
      window.location.hash = `#${sectionId}`;
    }
  }

  /**
   * Handle hash links when page loads
   */
  handleInitialHash() {
    // Check if there's a hash in the URL
    if (window.location.hash) {
      const hash = window.location.hash.slice(1);
      
      // Wait a bit for all content to load
      setTimeout(() => {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - this.headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }
}

// Initialize scroll-to-section functionality
if (typeof window !== 'undefined') {
  new ScrollToSection();
}

